import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Fade, 
  Slide,
  Chip,
  LinearProgress,
  useTheme
} from "@mui/material";
import { 
  TrendingUp, 
  AccountBalance, 
  ShowChart, 
  Security,
  Assessment,
  Business,
  People,
  Settings
} from "@mui/icons-material";

export const InitialLoadSkeleton = ({ message = "Initializing Vantage Alpha" }) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const loadingSteps = [
    { 
      title: "Initializing System", 
      description: "Setting up core components and configurations",
      icon: Settings,
      color: "#1976d2"
    },
    { 
      title: "Loading Market Data", 
      description: "Connecting to financial data sources",
      icon: TrendingUp,
      color: "#2e7d32"
    },
    { 
      title: "Preparing Portfolio", 
      description: "Loading your investment portfolio and strategies",
      icon: AccountBalance,
      color: "#ed6c02"
    },
    { 
      title: "Setting Up Analytics", 
      description: "Initializing performance tracking and reporting",
      icon: ShowChart,
      color: "#9c27b0"
    },
    { 
      title: "Security Check", 
      description: "Verifying authentication and permissions",
      icon: Security,
      color: "#d32f2f"
    },
    { 
      title: "Finalizing Setup", 
      description: "Completing initialization and preparing dashboard",
      icon: Assessment,
      color: "#1976d2"
    }
  ];

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 0.5; // Slower progress increment
      });
    }, 100); // Slower progress update (was 50ms)

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= loadingSteps.length - 1) {
          clearInterval(stepInterval);
          return loadingSteps.length - 1;
        }
        return prev + 1;
      });
    }, 3000); // Much slower step changes - 3 seconds per step (was 800ms)

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
    };
  }, []);

  const CurrentIcon = loadingSteps[currentStep]?.icon || Settings;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) =>
          theme.palette.mode === "light"
            ? "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
            : "linear-gradient(135deg, #0f1419 0%, #1a2332 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)",
          animation: "float 6s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
            "50%": { transform: "translateY(-20px) rotate(180deg)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(66, 165, 245, 0.1) 0%, rgba(25, 118, 210, 0.1) 100%)",
          animation: "float 8s ease-in-out infinite reverse",
        }}
      />

      {/* Main loading card */}
      <Fade in={true} timeout={1000}>
        <Slide direction="up" in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              zoom: 0.75,
              overflow: "hidden",
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.95)"
                  : "rgba(30, 40, 50, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              maxWidth: 500,
              width: "100%",
              position: "relative",
              zIndex: 10,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                color: "white",
                p: 4,
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative pattern */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                 
                  right: 0,
                  bottom: 0,
                  background: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"25\" cy=\"25\" r=\"1\" fill=\"rgba(255,255,255,0.1)\"/><circle cx=\"75\" cy=\"75\" r=\"1\" fill=\"rgba(255,255,255,0.1)\"/><circle cx=\"50\" cy=\"10\" r=\"0.5\" fill=\"rgba(255,255,255,0.1)\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>')",
                  opacity: 0.3,
                }}
              />
              
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                Vantage Alpha
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                Portfolio Management and Order Management
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 4 }}>
              {/* Current step indicator */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${loadingSteps[currentStep]?.color} 0%, ${loadingSteps[currentStep]?.color}dd 100%)`,
                    color: "white",
                    mb: 2,
                    boxShadow: `0 8px 32px ${loadingSteps[currentStep]?.color}40`,
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.05)" },
                    },
                  }}
                >
                  <CurrentIcon sx={{ fontSize: 40 }} />
                </Box>
                
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {loadingSteps[currentStep]?.title}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 3,
                    maxWidth: 300,
                    mx: "auto",
                  }}
                >
                  {loadingSteps[currentStep]?.description}
                </Typography>

                {/* Progress bar */}
                <Box sx={{ width: "100%", mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.grey[200],
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${loadingSteps[currentStep]?.color} 0%, ${loadingSteps[currentStep]?.color}dd 100%)`,
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                  }}
                >
                  {Math.round(progress)}% Complete
                </Typography>
              </Box>

              {/* Loading steps indicator */}
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                {loadingSteps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: index <= currentStep 
                        ? step.color 
                        : theme.palette.grey[300],
                      transition: "all 0.3s ease",
                      transform: index === currentStep ? "scale(1.2)" : "scale(1)",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Slide>
      </Fade>

      {/* Floating feature cards */}
      <Fade in={true} timeout={2000}>
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            left: "10%",
            zIndex: 5,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 2,
              zoom: 0.75,
              borderRadius: 2,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: (theme) =>
                theme.palette.mode === "light"
                  ? "1px solid rgba(25, 118, 210, 0.2)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              animation: "slideInLeft 1s ease-out 0.5s both",
              "@keyframes slideInLeft": {
                "0%": { transform: "translateX(-100px)", opacity: 0 },
                "100%": { transform: "translateX(0)", opacity: 1 },
              },
            }}
          >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingUp sx={{ color: "#2e7d32", fontSize: 20 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (theme) => theme.palette.mode === "light" ? "#1976d2" : "white", 
                  fontWeight: 600 
                }}
              >
                Real-time Analytics
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>

      <Fade in={true} timeout={2500}>
        <Box
          sx={{
            position: "absolute",
            bottom: "25%",
            right: "15%",
            zIndex: 5,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 2,
              borderRadius: 2,
              zoom: 0.75,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: (theme) =>
                theme.palette.mode === "light"
                  ? "1px solid rgba(25, 118, 210, 0.2)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              animation: "slideInRight 1s ease-out 1s both",
              "@keyframes slideInRight": {
                "0%": { transform: "translateX(100px)", opacity: 0 },
                "100%": { transform: "translateX(0)", opacity: 1 },
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Business sx={{ color: "#1976d2", fontSize: 20 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (theme) => theme.palette.mode === "light" ? "#1976d2" : "white", 
                  fontWeight: 600 
                }}
              >
                Multi-Strategy Support
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* Additional feature cards */}
      <Fade in={true} timeout={3000}>
        <Box
          sx={{
            position: "absolute",
            top: "60%",
            left: "5%",
            zIndex: 5,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 2,
              borderRadius: 2,  
              zoom: 0.75,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: (theme) =>
                theme.palette.mode === "light"
                  ? "1px solid rgba(25, 118, 210, 0.2)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              animation: "slideInLeft 1s ease-out 1.5s both",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalance sx={{ color: "#ed6c02", fontSize: 20 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (theme) => theme.palette.mode === "light" ? "#1976d2" : "white", 
                  fontWeight: 600 
                }}
              >
                Portfolio Management
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>

      <Fade in={true} timeout={3500}>
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            right: "8%",
            zIndex: 5,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 2,
              borderRadius: 2,
              zoom: 0.75,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: (theme) =>
                theme.palette.mode === "light"
                  ? "1px solid rgba(25, 118, 210, 0.3)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              animation: "slideInRight 1s ease-out 2s both",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ShowChart sx={{ color: "#9c27b0", fontSize: 20 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (theme) => theme.palette.mode === "light" ? "#1976d2" : "white", 
                  fontWeight: 600 
                }}
              >
                Advanced Reporting
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>

      <Fade in={true} timeout={4000}>
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "20%",
            zIndex: 5,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 2,
              zoom: 0.75,
              borderRadius: 2,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: (theme) =>
                theme.palette.mode === "light"
                  ? "1px solid rgba(25, 118, 210, 0.2)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              animation: "slideInLeft 1s ease-out 2.5s both",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <People sx={{ color: "#2e7d32", fontSize: 20 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (theme) => theme.palette.mode === "light" ? "#1976d2" : "white", 
                  fontWeight: 600 
                }}
              >
                Team Collaboration
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Box>
  );
}; 